// Cycle-16 voice-message bubble. Mirrors `PingVoiceMessageBubble`
// (iOS-A) and `VoiceMessageBubble` (AND-A). Renders one of seven voice
// states from the Figma `Voice message` (`3412:4708`) /
// `Voice message / failed` (`3412:4981`) frames. Pure controlled view —
// host owns playback state. The waveform redraw budget is throttled by
// snapping `progress` to a 33 ms grid (≈ 30 fps) so the bar `Equatable`
// memo elides identical renders.
//
// Figma node geometry (3412:4708 / 3412:4981):
//   Outer bubble  (3412:4711 / 3412:4984): 268×54, radius 16, padding 4
//   Inner overlay (3412:4712 / 3412:4985): 260×46, radius 12, padding 4,
//                                          fill rgba(0,0,0,0.2)
//   Play button   (3412:4714)            : 32×32, transparent
//   Waveform      (3412:4717)            : gap 5, bars 2 px wide,
//                                          heights 6/10/14 px,
//                                          fill #E8EAED
//   Duration text (3412:4749)            : 14 px Lato 400, 19.6 line-h
//   Failed copy   (3412:4989/4991/4997)  : "Failed to send" 12 pt Bold,
//                                          subtitle 12 pt 400 / 40 %,
//                                          error 10 pt 400 rose

import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { FileText, Info, Pause, Play, RotateCw } from 'lucide-react-native';
import { ReadReceiptOverlay, type ReadByReader } from './ReadReceiptOverlay';
import { tokens } from '../design-system/tokens';
import { useVoicePlayback } from './composer/useVoicePlayback';
import { useVoicePlaybackDriver } from './composer/VoicePlaybackProvider';
import type { VoiceSource } from './composer/voicePlaybackDriver';
import { formatClockTime, formatDuration, snapProgressToFrame } from './messageFormatters';
import { useTileMaxWidth } from './tileConstraint';
import type { Message } from '../sync/persistence';
import type { VoiceMessageState } from './VoiceMessageState';

export interface VoiceMessageBubbleProps {
  state: VoiceMessageState;
  durationMs: number;
  waveform: ReadonlyArray<number>;
  progress: number;
  timestamp: Date;
  isSelf: boolean;
  onTogglePlay: () => void;
  onRetry?: (() => void) | undefined;
  testID?: string | undefined;
  /**
   * Opt-in playback integration. When supplied AND a
   * `<VoicePlaybackProvider>` is mounted upstream, the bubble drives
   * its own playback state through the shared `useVoicePlayback` hook
   * — `progress`, `waveform`, and `onTogglePlay` props are overridden
   * by the live hook output. Without these props (or without a
   * provider) the bubble stays controlled — same legacy behaviour the
   * Figma snapshots are pinned against.
   */
  message?: Message | undefined;
  resolveVoiceSource?: ((message: Message) => VoiceSource | null) | undefined;
  /** Surfaced when playback fails to start (e.g. a toast). */
  onPlaybackError?: ((err: unknown) => void) | undefined;
  /**
   * Suppresses the in-tile meta row (timestamp). Used by the
   * stacked/compact layout where the timestamp lives in the row's
   * sender header instead of inside the tile.
   */
  hideMeta?: boolean | undefined;
  /** Own + delivered → muted double-check in the meta row. */
  delivered?: boolean | undefined;
  /** Own + read → green double-check in the meta row (takes precedence over `delivered`). */
  read?: boolean | undefined;
  /** Group chats only — resolved reader list for the "Read by" popover. */
  readers?: ReadByReader[] | undefined;
  /**
   * Compact/stacked layout — render left-aligned (no `flex-end` for
   * self) since stacked rows are all on the left.
   */
  stacked?: boolean | undefined;
  /**
   * Optional decoded transcript. When provided, the transcribe button
   * toggles an expand/collapse region below the waveform (Figma
   * 3168:993). Without it the button stays a passive affordance.
   */
  transcript?: string | undefined;
}

function isPlayingState(state: VoiceMessageState): boolean {
  return state === 'playing' || state === 'recording';
}

// Playback-speed cycle for the "1x" control (Figma 3238:11268).
const SPEED_CYCLE: ReadonlyArray<number> = [1, 1.5, 2];

export const VoiceMessageBubble = React.memo<VoiceMessageBubbleProps>(
  ({
    state,
    durationMs,
    waveform,
    progress,
    timestamp,
    isSelf,
    onTogglePlay,
    onRetry,
    testID,
    message,
    resolveVoiceSource,
    onPlaybackError,
    hideMeta,
    stacked,
    transcript,
    delivered,
    read,
    readers,
  }) => {
    const scheme = useColorScheme();
    // Thread side panel: cap the bubble to the given width and, since the bars
    // are a fixed 2px + 5px gap, re-sample the waveform down to what fits (the
    // "voice visualizer responsive to the panel" requirement) so bars never
    // overflow the narrower tile.
    const tileMax = useTileMaxWidth();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];
    const [speedIndex, setSpeedIndex] = useState(0);
    const [transcriptOpen, setTranscriptOpen] = useState(false);

    // Host-driven playback path. Hooks are called unconditionally so
    // React's rules-of-hooks ordering is stable; the resolver returns
    // `null` when the bubble isn't opted-in or no provider is mounted,
    // which short-circuits the hook into a fully idle no-op.
    const driver = useVoicePlaybackDriver();
    const playbackEnabled =
      driver !== null && message !== undefined && resolveVoiceSource !== undefined;
    const noopMessage = useMemo<Message>(
      () =>
        message ??
        ({
          id: '',
          conversationId: '',
          senderId: '',
          body: '',
          hlc: '',
          contentHash: '',
          timestamp: 0,
          isSelf: false,
        } as Message),
      [message],
    );
    const playbackResolver = useMemo(
      () =>
        playbackEnabled && resolveVoiceSource !== undefined
          ? resolveVoiceSource
          : () => null,
      [playbackEnabled, resolveVoiceSource],
    );
    const playback = useVoicePlayback({
      message: noopMessage,
      resolveSource: playbackResolver,
      ...(onPlaybackError !== undefined ? { onError: onPlaybackError } : {}),
    });

    // Pick the live hook output when the host opted-in; otherwise stay
    // on the explicit controlled props (legacy contract). Prefer the
    // envelope's recorded duration; fall back to the real duration the
    // driver learns from the audio element (MediaRecorder webm often
    // omits duration metadata, leaving the prop at 0 — without this the
    // progress fraction would be stuck at 0 and the bars never advance).
    const effectiveDurationMs =
      durationMs > 0 ? durationMs : playback.durationMs;
    const effectiveProgress = playbackEnabled
      ? effectiveDurationMs > 0
        ? Math.min(1, Math.max(0, playback.progressMs / effectiveDurationMs))
        : 0
      : progress;
    const effectiveWaveform: ReadonlyArray<number> = playbackEnabled
      ? playback.waveform.length > 0
        ? playback.waveform
        : waveform
      : waveform;
    const effectiveTogglePlay = playbackEnabled ? playback.togglePlay : onTogglePlay;
    const effectiveState: VoiceMessageState = playbackEnabled
      ? playback.isPlaying
        ? 'playing'
        : state === 'playing'
          ? 'paused'
          : state
      : state;

    const snappedProgress = useMemo(
      () => snapProgressToFrame(effectiveProgress, effectiveDurationMs),
      [effectiveProgress, effectiveDurationMs],
    );

    // The waveform fills the space between the play disc and the duration; we
    // measure its ACTUAL rendered width (`onLayout`) and render exactly the
    // number of bars that fill it, so the visualizer is 100% responsive at ANY
    // tile size — self/other, bubble/compact, thread/main chat, window resize.
    const [waveformWidth, setWaveformWidth] = useState<number | null>(null);
    // ~7px per column (2px bar + ~5px gap). Bounded so a very wide/narrow tile
    // still reads as a waveform. Before the first layout pass, fall back to a
    // sensible count so the initial paint isn't empty.
    const BAR_PITCH = 7;
    const barCount = useMemo(() => {
      const w = waveformWidth ?? 0;
      if (w <= 0) return Math.min(Math.max(effectiveWaveform.length, 8), 40);
      return Math.max(8, Math.min(64, Math.floor(w / BAR_PITCH)));
    }, [waveformWidth, effectiveWaveform.length]);

    // Re-sample the source waveform to exactly `barCount` columns. Evenly-spaced
    // picks preserve the overall shape; upsampling (barCount > source length)
    // repeats samples. `space-between` on the row then distributes the fixed-
    // width bars across the full measured width.
    const displayWaveform = useMemo(() => {
      const src = effectiveWaveform;
      if (src.length === 0) return [] as number[];
      const out: number[] = [];
      for (let i = 0; i < barCount; i += 1) {
        out.push(src[Math.floor((i * src.length) / barCount)] ?? 0);
      }
      return out;
    }, [effectiveWaveform, barCount]);

    // Memoise the bar array on the snapped progress so per-frame
    // redraws coalesce to one update at the 33 ms throttle budget.
    const bars = useMemo(() => {
      const playedBars = Math.max(1, Math.round(displayWaveform.length * snappedProgress));
      return displayWaveform.map((value, index) => ({
        value: Math.min(1, Math.max(0.08, value)),
        played: index < playedBars,
      }));
    }, [displayWaveform, snappedProgress]);

    const isFailed = effectiveState === 'failed';

    // Figma 7038:1649 (sent) fills the bubble with the indigo accent;
    // the received bubble uses the dark #222429 (surface.elevated) swatch —
    // the same "other-bubble" surface as text/doc/image tiles. The failed
    // state does NOT recolour the bubble itself — only the small
    // "Error sending voice message." caption below.
    const bubbleBackground = isSelf
      ? colors.accent.brand
      : colors.surface.elevated;
    const foreground = isSelf ? colors.text.onAccent : colors.text.primary;

    // The inner 260×46 frame (Figma 3412:4712 / 3412:4985) is a
    // black-20 % overlay sitting inside the indigo wrap. It gives the
    // waveform a slight depth lift without re-tinting the bubble.
    const innerOverlay = 'rgba(0, 0, 0, 0.2)';

    // Figma 3412:4708: played bars are the bright `#E8EAED` swatch at
    // full opacity; unplayed bars are white@40% over the colored/dark
    // bubble. Received played bars use the indigo accent for contrast
    // against the bright disc.
    const playedBarColor = isSelf ? '#E8EAED' : colors.accent.brand;
    const unplayedBarColor = 'rgba(255, 255, 255, 0.4)';

    // Play disc — Figma 3412:4714. Self: bright #E8EAED disc with an
    // indigo glyph. Received: indigo disc with a white glyph.
    const discColor = isSelf ? '#E8EAED' : colors.accent.brand;
    const discGlyphColor = isSelf ? colors.accent.brand : '#FFFFFF';
    const isPlaying = isPlayingState(effectiveState);
    const currentSpeed = SPEED_CYCLE[speedIndex] ?? 1;

    const accessibilityLabel = useMemo(() => {
      const baseLabel = `Voice message, ${formatDuration(durationMs)}`;
      if (isFailed) {
        return `${baseLabel}, failed to send`;
      }
      if (effectiveState === 'recording') {
        return 'Recording voice message';
      }
      return baseLabel;
    }, [effectiveState, durationMs, isFailed]);

    return (
      <View
        style={[
          styles.bubble,
          tileMax !== null ? { maxWidth: tileMax } : null,
          {
            backgroundColor: bubbleBackground,
            alignSelf: isSelf && !stacked ? 'flex-end' : 'flex-start',
          },
        ]}
        // The bubble's outer chrome is a passive container — the
        // interactive play/pause control lives inside as a
        // `<Pressable>`. We don't set `accessibilityRole="button"`
        // here because RN-Web translates it into a real `<button>`
        // element, which would nest another `<button>` (the
        // Pressable below) and trigger a DOM-nesting warning.
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ busy: isPlayingState(effectiveState) }}
        testID={testID}
      >
        <View style={[styles.inner, { backgroundColor: innerOverlay }]}>
          {isFailed ? (
            // Failed state — Figma 7038:1922. A circular retry button +
            // "Failed to send" (bold) over "Voice message 0:14" (subtitle).
            // NO waveform. The red caption renders below the tile.
            <Pressable
              onPress={onRetry}
              accessibilityRole="button"
              accessibilityLabel="Retry sending voice message"
              style={styles.row}
            >
              <View style={[styles.playButton, { backgroundColor: 'rgba(244, 244, 245, 0.08)' }]}>
                <RotateCw size={16} strokeWidth={2.5} color={foreground} />
              </View>
              <View style={styles.failedBody}>
                <Text style={[styles.failedTitle, { color: foreground }]} numberOfLines={1}>
                  Failed to send
                </Text>
                <Text
                  style={[
                    styles.failedSubtitle,
                    { color: isSelf ? 'rgba(255, 255, 255, 0.6)' : colors.text.secondary },
                  ]}
                  numberOfLines={1}
                >
                  {`Voice message ${formatDuration(durationMs)}`}
                </Text>
              </View>
            </Pressable>
          ) : (
          <View style={styles.row}>
            <Pressable
              onPress={effectiveTogglePlay}
              accessibilityRole="button"
              accessibilityLabel={isPlaying ? 'Pause voice message' : 'Play voice message'}
              style={[styles.playButton, { backgroundColor: discColor }]}
            >
              {isPlaying ? (
                <Pause size={16} strokeWidth={2.5} color={discGlyphColor} fill={discGlyphColor} />
              ) : (
                <Play size={16} strokeWidth={2.5} color={discGlyphColor} fill={discGlyphColor} />
              )}
            </Pressable>
            <View
              style={styles.waveform}
              onLayout={(e) => {
                const w = e.nativeEvent.layout.width;
                // Only update on a real change so we don't loop layout passes.
                setWaveformWidth((prev) =>
                  prev === null || Math.abs(prev - w) > 1 ? w : prev,
                );
              }}
            >
              {bars.map((bar, index) => (
                <View
                  key={index}
                  style={[
                    styles.bar,
                    {
                      height: 12 * bar.value + 6,
                      backgroundColor: bar.played ? playedBarColor : unplayedBarColor,
                    },
                  ]}
                />
              ))}
            </View>
            <Text
              style={[
                styles.duration,
                { color: isSelf ? 'rgba(255, 255, 255, 0.6)' : colors.text.secondary },
              ]}
            >
              {formatDuration(durationMs)}
            </Text>
            {/* Controls — Figma 3412:4708 "Frame 1000005035": transcribe
                button + 1x/1.5x/2x playback-speed pill. */}
            <View style={styles.controls}>
              <Pressable
                onPress={() => setTranscriptOpen((v) => !v)}
                accessibilityRole="button"
                accessibilityLabel="Transcribe voice message"
                style={styles.controlButton}
                hitSlop={6}
              >
                <FileText
                  size={18}
                  strokeWidth={2}
                  color={isSelf ? 'rgba(255,255,255,0.85)' : colors.text.secondary}
                />
              </Pressable>
              <Pressable
                onPress={() => {
                  const nextIndex = (speedIndex + 1) % SPEED_CYCLE.length;
                  setSpeedIndex(nextIndex);
                  // Apply the new speed to the live audio element (and seed
                  // the next play) when on the shared-playback path.
                  if (playbackEnabled) {
                    playback.setRate(SPEED_CYCLE[nextIndex] ?? 1);
                  }
                }}
                accessibilityRole="button"
                accessibilityLabel={`Playback speed ${currentSpeed}x`}
                style={styles.speedPill}
                hitSlop={6}
              >
                <Text
                  style={[
                    styles.speedText,
                    { color: isSelf ? '#FFFFFF' : colors.text.secondary },
                  ]}
                >
                  {`${currentSpeed}x`}
                </Text>
              </Pressable>
            </View>
          </View>
          )}
          {transcriptOpen && !isFailed ? (
            <View style={styles.transcriptWrap}>
              <View
                style={[
                  styles.transcriptDivider,
                  { backgroundColor: isSelf ? 'rgba(255,255,255,0.2)' : colors.border.primary },
                ]}
              />
              {transcript !== undefined && transcript.length > 0 ? (
                <Text style={[styles.transcriptText, { color: foreground }]}>
                  {transcript}
                </Text>
              ) : (
                // Give the transcribe button visible feedback even when no
                // transcript was captured (short clip / unsupported browser).
                <Text
                  style={[
                    styles.transcriptText,
                    {
                      color: isSelf ? 'rgba(255,255,255,0.6)' : colors.text.secondary,
                      fontStyle: 'italic',
                    },
                  ]}
                >
                  No transcription available.
                </Text>
              )}
            </View>
          ) : null}
        </View>

        {isFailed ? (
          // Error caption — Figma 3412:4981/4643: left-aligned rose
          // text + a 10px info-circle below the tile.
          <View style={styles.errorRow}>
            <Text style={[styles.errorText, { color: '#FF5353' }]}>
              Error sending voice message
            </Text>
            <Info size={10} strokeWidth={2} color="#FF5353" />
          </View>
        ) : hideMeta ? null : (
          <View style={styles.metaRow}>
            <ReadReceiptOverlay
              timeLabel={formatClockTime(timestamp)}
              textStyle={{ color: isSelf ? 'rgba(255, 255, 255, 0.6)' : colors.text.tertiary }}
              {...(delivered !== undefined ? { delivered } : {})}
              {...(read !== undefined ? { read } : {})}
              isSelf={isSelf === true}
              {...(readers !== undefined ? { readers } : {})}
              showPill={false}
              tickColor={read === true ? '#3CCB4F' : isSelf ? 'rgba(255,255,255,0.6)' : '#FFFFFF'}
              popoverPlacement="top"
              popoverAlign={isSelf ? 'end' : 'start'}
            />
          </View>
        )}
      </View>
    );
  },
);

VoiceMessageBubble.displayName = 'VoiceMessageBubble';

const styles = StyleSheet.create({
  // Outer bubble — Figma 3412:4711 (268 wide, radius 16, padding 4).
  // `width` (not `minWidth`) so the tile sizes to 268 in the main chat
  // but SHRINKS below that inside a narrow container (e.g. the thread
  // panel) instead of overflowing its rounded corners off-screen.
  bubble: {
    // A definite width (Figma 3412:4711) so the waveform's `flex: 1` has real
    // space to fill and can be measured — WITHOUT it the bubble hugs the bars'
    // intrinsic width and the visualizer never becomes responsive. `maxWidth`
    // still shrinks it below 268 inside a narrow container (thread panel), and
    // the measured-width bar re-sample keeps it filled at whatever size wins.
    width: 268,
    maxWidth: '100%',
    padding: tokens.spacing.xs,
    borderRadius: 16,
    gap: tokens.spacing.xs,
  },
  // Inner dark-20 % overlay — Figma 3412:4712 (260×46, radius 12,
  // padding 4 top/L/R, gap -4 — we render a flat row here).
  inner: {
    borderRadius: 12,
    paddingHorizontal: tokens.spacing.xs,
    paddingVertical: tokens.spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  // Failed state body — "Failed to send" (12/700) over "Voice message
  // 0:14" (12/400). Figma 7038:1922.
  failedBody: {
    flexShrink: 1,
    flexGrow: 1,
  },
  failedTitle: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: '700',
  } as TextStyle,
  failedSubtitle: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 12,
    lineHeight: 16,
    marginTop: 2,
  } as TextStyle,
  // Play disc — Figma 3412:4714 (32×32 filled circle).
  playButton: {
    width: 32,
    height: 32,
    borderRadius: tokens.radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Waveform — Figma 3412:4717 (gap 5, bars 2 px, heights 6-18).
  waveform: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    // Distribute the fixed-width bars across the FULL measured width so the
    // visualizer always fills the space edge-to-edge (responsive). `barCount`
    // is derived from the measured width so the gaps land near the 5px design.
    justifyContent: 'space-between',
    height: 18,
  },
  bar: {
    width: 2,
    borderRadius: 2,
  },
  // Controls — Figma 3412:4708 "Frame 1000005035" (transcribe + speed).
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xxs,
  },
  controlButton: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  speedPill: {
    minWidth: 26,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  speedText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '600',
  } as TextStyle,
  transcriptWrap: {
    paddingHorizontal: tokens.spacing.xs,
    paddingTop: tokens.spacing.xs,
    gap: tokens.spacing.xs,
  },
  transcriptDivider: {
    height: 1,
    alignSelf: 'stretch',
  },
  transcriptText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 14,
    lineHeight: 20,
  } as TextStyle,
  // Duration — Figma 3412:4749 (14 pt Lato 400, line-h 19.6).
  duration: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 14,
    lineHeight: 20,
    minWidth: 36,
    textAlign: 'right',
  } as TextStyle,
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.xs,
  },
  // Error caption row — left-aligned (Figma 3412:4981/4643).
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xxs,
    paddingHorizontal: tokens.spacing.xs,
    paddingTop: 2,
  },
  // Error caption — Figma 3412:4997 (10 pt Lato 400, rose).
  errorText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 10,
    lineHeight: 14,
    fontWeight: '400',
  },
  // Timestamp — Figma I3412:4769;578:4651 (10 pt Lato 400).
  timestamp: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 10,
    lineHeight: 14,
  },
});
